import {
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
  type CLSMetricWithAttribution,
  type FCPMetricWithAttribution,
  type INPMetricWithAttribution,
  type LCPMetricWithAttribution,
  type TTFBMetricWithAttribution,
} from 'web-vitals/attribution'
import { CLS_VERSION } from './00.aa-dev-cls'
const CLS_ATTRIBUTION_THRESHOLD = 0.1
const MAX_SHIFT_SNAPSHOTS = 100
const MAX_REPORTED_SHIFTS = 10
const MAX_TIMELINE_ITEMS = 50
type RouteLike = {
  name?: unknown
  fullPath?: string
  path?: string
}
type RouteSnapshot = {
  name?: string
  fullPath?: string
  path?: string
}
type TimelineItem<T> = T & { at: number }
type LoadState = 'loading' | 'dom-interactive' | 'dom-content-loaded' | 'complete'
function limitString(value: string | null | undefined, max = 180): string | undefined {
  if (!value) return
  return value.length > max ? `${value.slice(0, max)}…` : value
}
function toRouteSnapshot(route: RouteLike | null | undefined): RouteSnapshot | undefined {
  if (!route) return
  const name = typeof route.name === 'string' ? route.name : route.name != null ? String(route.name) : undefined
  return {
    name: limitString(name),
    fullPath: limitString(route.fullPath, 500),
    path: limitString(route.path, 300),
  }
}
function toLogRoute(route: RouteSnapshot | undefined): gen.AppLogsInputLogRoute | undefined {
  if (!route) return
  return {
    name: route.name,
    fullPath: route.fullPath,
    path: route.path,
  }
}
function toRect(rect: DOMRectReadOnly | null | undefined) {
  if (!rect) return
  return {
    x: rect.x,
    y: rect.y,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}
function getLoadStateAt(timestamp: number): LoadState {
  if (document.readyState === 'loading') return 'loading'
  const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  if (navigationEntry) {
    if (timestamp < navigationEntry.domInteractive) return 'loading'
    if (navigationEntry.domContentLoadedEventStart === 0 || timestamp < navigationEntry.domContentLoadedEventStart) return 'dom-interactive'
    if (navigationEntry.domComplete === 0 || timestamp < navigationEntry.domComplete) return 'dom-content-loaded'
  }
  return 'complete'
}
function toElemStr(node: Element): string {
  const tag = node.tagName.toLowerCase()
  const layoutAttribute = node.hasAttribute('data-layout-id') ? 'data-layout-id' : node.hasAttribute('data-cls') ? 'data-cls' : undefined
  const layoutId = limitString(layoutAttribute ? node.getAttribute(layoutAttribute) : undefined, 100)
  if (layoutAttribute && layoutId) return `${tag}[${layoutAttribute}="${layoutId.replaceAll('"', '\\"')}"]`
  if (node.id) return `${tag}#${limitString(node.id, 100)}`
  const classes = Array.from(node.classList).filter(Boolean).slice(0, 4)
  const clsMarker = classes.find((value) => value.startsWith('cls-'))
  if (clsMarker) return `${tag}.${clsMarker}`
  if (classes.length) return `${tag}.${classes.join('.')}`
  if (node.parentElement) return `${tag}:nth-child(${Array.from(node.parentElement.children).indexOf(node) + 1})`
  return tag
}
function getSelectorPath(node: unknown): string {
  if (!(node instanceof Element)) return '[non-element node]'
  const parts: string[] = []
  let current: Element | null = node
  while (current && parts.length < 8) {
    parts.unshift(toElemStr(current))
    if (current.tagName.toLowerCase() === 'html' || current.id || current.hasAttribute('data-layout-id') || current.hasAttribute('data-cls')) break
    current = current.parentElement
  }
  return limitString(parts.join(' > '), 600) || '[unknown]'
}
function toComputedLayout(node: Element) {
  try {
    const style = window.getComputedStyle(node)
    return {
      display: style.display,
      position: style.position,
      width: style.width,
      height: style.height,
      minHeight: style.minHeight,
      maxHeight: style.maxHeight,
      overflow: style.overflow,
      contain: style.contain,
      contentVisibility: style.contentVisibility,
      transitionProperty: style.transitionProperty,
      transitionDuration: style.transitionDuration,
      animationName: style.animationName,
    }
  } catch {
    return undefined
  }
}
function stringifyNode(node: unknown) {
  if (!node) return
  if (!(node instanceof Element)) {
    if (!(node instanceof Node)) return { type: typeof node }
    return {
      nodeType: node.nodeType,
      nodeName: limitString(node.nodeName, 100),
      parentPath: node.parentElement ? getSelectorPath(node.parentElement) : undefined,
    }
  }
  let rectAtObserver: ReturnType<typeof toRect>
  try {
    rectAtObserver = toRect(node.getBoundingClientRect())
  } catch {
    rectAtObserver = undefined
  }
  const media =
    node instanceof HTMLImageElement
      ? {
          kind: 'image',
          complete: node.complete,
          naturalWidth: node.naturalWidth,
          naturalHeight: node.naturalHeight,
          widthAttribute: node.getAttribute('width') || undefined,
          heightAttribute: node.getAttribute('height') || undefined,
        }
      : node instanceof HTMLVideoElement
        ? {
            kind: 'video',
            readyState: node.readyState,
            videoWidth: node.videoWidth,
            videoHeight: node.videoHeight,
            widthAttribute: node.getAttribute('width') || undefined,
            heightAttribute: node.getAttribute('height') || undefined,
          }
        : undefined
  return {
    tag: node.tagName.toLowerCase(),
    id: limitString(node.id, 100),
    layoutId: limitString(node.getAttribute('data-layout-id') || node.getAttribute('data-cls'), 100),
    selector: toElemStr(node),
    path: getSelectorPath(node),
    rectAtObserver,
    clientWidth: node.clientWidth,
    clientHeight: node.clientHeight,
    scrollWidth: node.scrollWidth,
    scrollHeight: node.scrollHeight,
    layout: toComputedLayout(node),
    media,
  }
}
function stringifyPrecedingContext(node: Node | null | undefined) {
  if (!(node instanceof Element)) return
  let current: Element | null = node
  for (let depth = 0; current && depth < 6; depth += 1) {
    if (current.previousElementSibling) {
      return {
        relationship: depth === 0 ? 'previous-sibling' : 'previous-sibling-of-ancestor',
        ancestorDepth: depth,
        node: stringifyNode(current.previousElementSibling),
      }
    }
    current = current.parentElement
  }
}
function stringifySource(source: LayoutShiftAttribution) {
  return {
    node: stringifyNode(source.node),
    parent: stringifyNode(source.node?.parentElement),
    precedingContext: stringifyPrecedingContext(source.node),
    currentRect: toRect(source.currentRect),
    previousRect: toRect(source.previousRect),
  }
}
function stringifyLayoutShift(entry: LayoutShift, add?: Record<string, unknown>) {
  return {
    entryType: entry.entryType,
    name: entry.name,
    duration: entry.duration,
    startTime: entry.startTime,
    occurredAt: new Date(performance.timeOrigin + entry.startTime).toISOString(),
    value: entry.value,
    hadRecentInput: entry.hadRecentInput,
    sources: entry.sources.map((source) => stringifySource(source)),
    ...add,
  }
}
function serializeError(error: unknown): unknown {
  if (error instanceof Error) return { name: error.name, message: error.message, stack: error.stack }
  return error
}
function toMetric(metric: CLSMetricWithAttribution) {
  return {
    name: metric.name,
    value: metric.value,
    delta: metric.delta,
    id: metric.id,
    rating: metric.rating,
    navigationType: metric.navigationType,
  }
}
export default defineNuxtPlugin({
  parallel: false,
  async setup(nuxtApp) {
    const { track } = useAnalytics()
    const route = useRoute()
    const allData = (): gen.AppLogsInputLog => ({
      screenHeight: window.innerHeight,
      screenWidth: window.innerWidth,
      referrer: window.document?.referrer || undefined,
      timezone: helpers.tryCatch(() => ('Intl' in window ? window.Intl.DateTimeFormat().resolvedOptions().timeZone : undefined)),
    })
    track(() => ({ action: 'Enter', ...allData() }))
    const scrollTop = stateUseScrollTop()
    const offScroll = watch(scrollTop, (newTop) => {
      if (newTop > 100) {
        track(() => ({ action: 'Scroll' }))
        offScroll()
      }
    })
    let exitDone = false
    for (const type of ['beforeunload', 'pagehide']) {
      window.addEventListener(type, () => {
        if (exitDone) return
        exitDone = true
        track(() => ({ action: 'Exit' }))
      })
    }
    function trackError(cb: () => unknown[]) {
      track(() => {
        let name = 'console:error'
        const err: unknown[] = []
        cb().forEach((item, index) => {
          if (index === 0) {
            if (typeof item === 'string') return (name = item)
            if (item instanceof Error && typeof item.message === 'string') name = item.message
          }
          err.push(serializeError(item))
        })
        return { name, errJson: stringifyCircular(err.length === 1 ? err[0] : err), ...allData() }
      })
    }
    const originalError = console.error
    console.error = (...data) => {
      originalError.apply(console, data)
      trackError(() => data)
    }
    const originalWarn = console.warn
    console.warn = (...data) => {
      if (helpers.toString(data[0])?.startsWith('[Vue warn]')) {
        trackError(() => helpers.arr(data, (value) => (typeof value === 'string' ? value : stringifyNode(value))))
      }
      originalWarn.apply(console, data)
    }
    nuxtApp.hook('vue:error', (error, _, info) => trackError(() => ['vue:error', error, { info }]))
    nuxtApp.hook('app:error', (error) => trackError(() => ['app:error', error]))
    const createPageViewId = () => `${Math.round(performance.timeOrigin).toString(36)}-${Math.random().toString(36).slice(2, 12)}`
    let pageViewId = createPageViewId()
    const routeTimeline: TimelineItem<RouteSnapshot>[] = [{ at: 0, ...toRouteSnapshot(route) }]
    const toViewportSnapshot = () => ({
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      clientWidth: document.documentElement.clientWidth,
      clientHeight: document.documentElement.clientHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      devicePixelRatio: window.devicePixelRatio,
      visualViewport: window.visualViewport
        ? {
            width: window.visualViewport.width,
            height: window.visualViewport.height,
            offsetTop: window.visualViewport.offsetTop,
            offsetLeft: window.visualViewport.offsetLeft,
            scale: window.visualViewport.scale,
          }
        : undefined,
    })
    const viewportTimeline: TimelineItem<ReturnType<typeof toViewportSnapshot>>[] = [{ at: 0, ...toViewportSnapshot() }]
    function addTimelineItem<T extends object>(timeline: TimelineItem<T>[], value: T) {
      timeline.push({ at: performance.now(), ...value })
      if (timeline.length > MAX_TIMELINE_ITEMS) timeline.splice(1, timeline.length - MAX_TIMELINE_ITEMS)
    }
    function timelineAt<T extends object>(timeline: TimelineItem<T>[], at: number): TimelineItem<T> | undefined {
      for (let index = timeline.length - 1; index >= 0; index -= 1) {
        const item = timeline[index]
        if (item && item.at <= at) return item
      }
      return timeline[0]
    }
    let viewportRecordFrame: number | undefined
    const recordViewport = () => {
      if (viewportRecordFrame !== undefined) return
      viewportRecordFrame = window.requestAnimationFrame(() => {
        viewportRecordFrame = undefined
        addTimelineItem(viewportTimeline, toViewportSnapshot())
      })
    }
    window.addEventListener('resize', recordViewport, { passive: true })
    window.addEventListener('scroll', recordViewport, { passive: true })
    window.visualViewport?.addEventListener('resize', recordViewport, { passive: true })
    window.visualViewport?.addEventListener('scroll', recordViewport, { passive: true })
    let lastInput:
      | {
          at: number
          type: string
          target?: string
        }
      | undefined
    function recordInput(event: Event) {
      const rawAt = event.timeStamp
      const at = rawAt > performance.timeOrigin ? rawAt - performance.timeOrigin : rawAt
      lastInput = {
        at,
        type: event.type,
        target: event.target instanceof Element ? getSelectorPath(event.target) : undefined,
      }
    }
    window.addEventListener('pointerdown', recordInput, { capture: true, passive: true })
    window.addEventListener('keydown', recordInput, { capture: true })
    // Record route changes before filtering which ones become analytics Move events.
    addRouteMiddleware((to, from) => {
      addTimelineItem(routeTimeline, toRouteSnapshot(to) || {})
      const skipAnalyticsHit = (): boolean => {
        if (!from?.fullPath || !to?.fullPath || !from.name) return true
        if (from.name !== to.name) return false
        if (!from.hash && !to.hash) return false
        if (from.fullPath.replace(from.hash, '') === to.fullPath.replace(to.hash, '')) return true
        return false
      }
      if (!skipAnalyticsHit()) track(({ toAppRouteLog }) => ({ action: 'Move', from: toAppRouteLog(from), to: toAppRouteLog(to) }))
    })
    try {
      function stringifyMetric(metric: LCPMetricWithAttribution | FCPMetricWithAttribution | TTFBMetricWithAttribution | INPMetricWithAttribution) {
        return stringifyCircular({
          value: metric.value,
          delta: metric.delta,
          id: metric.id,
          rating: metric.rating,
          navigationType: metric.navigationType,
          attribution: metric.attribution,
        })
      }
      onLCP((metric) => track(() => ({ name: 'LCP', eventJson: stringifyMetric(metric) })))
      onFCP((metric) => track(() => ({ name: 'FCP', eventJson: stringifyMetric(metric) })))
      onTTFB((metric) => track(() => ({ name: 'TTFB', eventJson: stringifyMetric(metric) })))
      onINP((metric) => track(() => ({ name: 'INP', eventJson: stringifyMetric(metric) })))
      const shiftSnapshots = new Map<string, ReturnType<typeof snapshotShift>>()
      const shiftSnapshotOrder: string[] = []
      const reportedAttributions = new Set<string>()
      function shiftKey(entry: LayoutShift): string {
        return `${entry.startTime.toFixed(3)}:${entry.value.toFixed(6)}:${entry.sources.length}`
      }
      function snapshotShift(entry: LayoutShift) {
        const at = entry.startTime
        const observerAt = performance.now()
        const entrySnapshot = stringifyLayoutShift(entry)
        const primarySource = entry.sources.find((source) => source.node?.nodeType === 1) || entry.sources[0]
        const primarySourceIndex = primarySource ? entry.sources.indexOf(primarySource) : -1
        const routeAtShift = timelineAt(routeTimeline, at)
        const viewportAtShift = timelineAt(viewportTimeline, at)
        const recentInput = lastInput && at >= lastInput.at && at - lastInput.at <= 30_000 ? lastInput : undefined
        return {
          entryKey: shiftKey(entry),
          entry: entrySnapshot,
          largestShiftSource: primarySourceIndex >= 0 ? entrySnapshot.sources[primarySourceIndex] : undefined,
          largestShiftTarget: primarySource?.node ? getSelectorPath(primarySource.node) : undefined,
          largestShiftTime: entry.startTime,
          largestShiftValue: entry.value,
          routeAtShift,
          routeAtObserver: toRouteSnapshot(route),
          viewportAtShift,
          viewportAtObserver: { at: observerAt, ...toViewportSnapshot() },
          recentViewportChanges: viewportTimeline.filter((item) => Math.abs(item.at - at) <= 1_000),
          lastInput: recentInput,
          inputToShiftMs: recentInput ? at - recentInput.at : undefined,
          observerDelayMs: Math.max(0, observerAt - at),
          loadStateAtShift: getLoadStateAt(at),
          readyStateAtObserver: document.readyState,
        }
      }
      function rememberShift(entry: LayoutShift) {
        const key = shiftKey(entry)
        const existing = shiftSnapshots.get(key)
        if (existing) return existing
        const snapshot = snapshotShift(entry)
        shiftSnapshots.set(key, snapshot)
        shiftSnapshotOrder.push(key)
        while (shiftSnapshotOrder.length > MAX_SHIFT_SNAPSHOTS) {
          const oldest = shiftSnapshotOrder.shift()
          if (oldest) shiftSnapshots.delete(oldest)
        }
        return snapshot
      }
      function selectReportedShifts(snapshots: ReturnType<typeof snapshotShift>[]) {
        if (snapshots.length <= MAX_REPORTED_SHIFTS) return snapshots
        const selected = new Map<string, ReturnType<typeof snapshotShift>>()
        const top = [...snapshots].sort((a, b) => b.largestShiftValue - a.largestShiftValue).slice(0, 5)
        const recent = snapshots.slice(-5)
        for (const snapshot of [...top, ...recent]) selected.set(snapshot.entryKey, snapshot)
        return [...selected.values()].sort((a, b) => a.largestShiftTime - b.largestShiftTime)
      }
      function reportAttribution(snapshot: ReturnType<typeof snapshotShift>, metric?: ReturnType<typeof toMetric>) {
        if (reportedAttributions.has(snapshot.entryKey)) return
        reportedAttributions.add(snapshot.entryKey)
        const reportPageViewId = pageViewId
        track(() => ({
          name: 'CLS-attribution',
          ...allData(),
          to: toLogRoute(snapshot.routeAtShift),
          eventJson: stringifyCircular({
            v: CLS_VERSION,
            pageViewId: reportPageViewId,
            entryKey: snapshot.entryKey,
            metric,
            largestShiftEntry: snapshot.entry,
            largestShiftSource: snapshot.largestShiftSource,
            largestShiftTarget: snapshot.largestShiftTarget,
            largestShiftTime: snapshot.largestShiftTime,
            largestShiftValue: snapshot.largestShiftValue,
            loadState: snapshot.loadStateAtShift,
            readyStateAtObserver: snapshot.readyStateAtObserver,
            routeAtShift: snapshot.routeAtShift,
            routeAtObserver: snapshot.routeAtObserver,
            viewportAtShift: snapshot.viewportAtShift,
            viewportAtObserver: snapshot.viewportAtObserver,
            recentViewportChanges: snapshot.recentViewportChanges,
            lastInput: snapshot.lastInput,
            inputToShiftMs: snapshot.inputToShiftMs,
            observerDelayMs: snapshot.observerDelayMs,
          }),
        }))
      }
      if (PerformanceObserver.supportedEntryTypes?.includes('layout-shift')) {
        const observer = new PerformanceObserver((list) => {
          for (const performanceEntry of list.getEntries()) {
            const entry = performanceEntry as LayoutShift
            if (entry.hadRecentInput) continue
            const snapshot = rememberShift(entry)
            if (entry.value >= CLS_ATTRIBUTION_THRESHOLD) reportAttribution(snapshot)
          }
        })
        observer.observe({ type: 'layout-shift', buffered: true })
      }
      onCLS((metric) => {
        // web-vitals can surface an empty zero metric around restored pages. It is not actionable CLS evidence.
        if (metric.value === 0 && metric.entries.length === 0) return
        const metricData = toMetric(metric)
        const attribution = metric.attribution
        const metricEntries = metric.entries.filter((entry) => !entry.hadRecentInput)
        const snapshots: ReturnType<typeof snapshotShift>[] = []
        for (const entry of metricEntries) {
          const snapshot = shiftSnapshots.get(shiftKey(entry))
          if (snapshot) snapshots.push(snapshot)
        }
        const largestSnapshot = attribution.largestShiftEntry ? shiftSnapshots.get(shiftKey(attribution.largestShiftEntry)) : undefined
        const nonGood = metric.rating !== 'good'
        const reportedShifts = nonGood ? selectReportedShifts(snapshots) : undefined
        const reportPageViewId = pageViewId
        const initialRoute = routeTimeline[0]
        const routeAtReport = toRouteSnapshot(route)
        // Preserve the existing CLS-attribution chart even when cumulative CLS is bad
        // because of several shifts smaller than 0.1.
        if (nonGood && largestSnapshot) reportAttribution(largestSnapshot, metricData)
        track(() => ({
          name: 'CLS',
          ...allData(),
          to: toLogRoute(largestSnapshot?.routeAtShift),
          eventJson: stringifyCircular({
            v: CLS_VERSION,
            pageViewId: reportPageViewId,
            metric: metricData,
            initialRoute,
            routeAtReport,
            largestShiftEntry: largestSnapshot?.entry,
            largestShiftSource: largestSnapshot?.largestShiftSource,
            largestShiftTarget: largestSnapshot?.largestShiftTarget || attribution.largestShiftTarget,
            largestShiftTime: attribution.largestShiftTime,
            largestShiftValue: attribution.largestShiftValue,
            loadState: attribution.loadState,
            shiftCount: metricEntries.length,
            snapshotMissingCount: Math.max(0, metricEntries.length - snapshots.length),
            reportedShiftCount: reportedShifts?.length || 0,
            omittedShiftCount: reportedShifts ? Math.max(0, metricEntries.length - reportedShifts.length) : metricEntries.length,
            shifts: reportedShifts,
          }),
        }))
        const consoleLabel = `[cls-${CLS_VERSION}]`
        if (import.meta.dev) {
          if (nonGood) originalWarn.apply(console, [consoleLabel, metricData, reportedShifts])
          else console.log(consoleLabel, metricData)
        } else if (nonGood) {
          console.log(consoleLabel, metricData)
        }
      })
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return
        pageViewId = createPageViewId()
        routeTimeline.splice(0, routeTimeline.length, { at: performance.now(), ...toRouteSnapshot(route) })
        viewportTimeline.splice(0, viewportTimeline.length, { at: performance.now(), ...toViewportSnapshot() })
        if (viewportRecordFrame !== undefined) window.cancelAnimationFrame(viewportRecordFrame)
        viewportRecordFrame = undefined
        lastInput = undefined
        shiftSnapshots.clear()
        shiftSnapshotOrder.splice(0)
        reportedAttributions.clear()
        exitDone = false
        track(() => ({ action: 'Enter', ...allData() }))
      })
    } catch (error) {
      console.error('[cls]', error)
    }
  },
})
